import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import CountUp from "react-countup";
import useReveal from "../hooks/useReveal";
import "./Home.css";
import { config } from "../config";

const MARQUEE_ITEMS = [
  "Website Development",
  "Digital Marketing",
  "Graphics Design",
  "UI/UX Design",
  "Brand Identity",
  "SEO Optimization",
  "Web Applications",
  "Motion Design",
];

const SERVICES = [
  {
    icon: "💻",
    title: "Website Development",
    path: "/services/website-development",
    desc: "Blazing-fast, pixel-perfect websites built with modern frameworks. From landing pages to full enterprise platforms.",
  },
  {
    icon: "📈",
    title: "Digital Marketing",
    path: "/services/digital-marketing",
    desc: "Data-driven campaigns that connect your brand with the right audience and deliver measurable ROI.",
  },
  {
    icon: "🎨",
    title: "Graphics Design",
    path: "/services/graphics-design",
    desc: "Graphic design combines visuals, typography, and creativity to communicate ideas, messages, and brand identity effectively.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Discovery",
    desc: "We deep-dive into your goals, audience, and competitive landscape to build a solid strategic foundation.",
  },
  {
    n: "02",
    title: "Strategy",
    desc: "A detailed roadmap is crafted aligning design, technology, and marketing to your business objectives.",
  },
  {
    n: "03",
    title: "Design",
    desc: "Wireframes evolve into high-fidelity prototypes that are polished, functional, and on-brand.",
  },
  {
    n: "04",
    title: "Launch",
    desc: "Rigorous QA, performance optimisation and a smooth go-live so your product hits the ground running.",
  },
];

const STATS = [
  { val: 100, suffix: "+", label: "Projects Delivered" },
  { val: 30, suffix: "+", label: "Happy Clients" },
  { val: 5, suffix: "+", label: "Years Experience" },
  { val: 10, suffix: "+", label: "Team Members" },
];

const TESTIMONIALS = [
  {
    text: "CubeX Technology made everything simple for us, from creative ideas to smooth execution and great final results.",
    name: "Saurabh Thakor",
    role: "Founder - Elementeers",
    init: "S",
  },
  {
    text: "The team at CubeX Technology was supportive, professional, and genuinely cared about delivering quality work.",
    name: "Kaksh Patel",
    role: "Director - Roliox Alloys",
    init: "K",
  },
  {
    text: "We loved working with CubeX Technology because their creativity, communication, and attention to detail truly stood out.",
    name: "Ravi",
    role: "Founder - Maa Vaishno Jewellers",
    init: "P",
  },
];

export default function Home() {
  useReveal();

  useEffect(() => {
    const wrap = document.getElementById("particles");
    if (!wrap) return;
    for (let i = 0; i < 18; i++) {
      const p = document.createElement("div");
      p.className = "particle";
      p.style.left = Math.random() * 100 + "%";
      p.style.animationDuration = 6 + Math.random() * 10 + "s";
      p.style.animationDelay = Math.random() * 8 + "s";
      p.style.width = p.style.height = Math.random() * 2 + 1 + "px";
      wrap.appendChild(p);
    }
    return () => {
      if (wrap) wrap.innerHTML = "";
    };
  }, []);

  const marqueeItems = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];

  return (
    <div className="home">
      {/* ── HERO ── */}
      <section id="hero">
        <div className="hero-bg" />
        <div className="hero-grid" />
        <div id="particles" className="particles" />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            padding: "0 5%",
          }}
        >
          <p className="hero-eyebrow">✦ Next-Gen Digital Solutions ✦</p>

          <h1 className="hero-title">
            Building Your Future
            <br />
            in the Digital Space with
            <br />
            <span className="g">CubeX</span>
          </h1>

          <p className="hero-subtitle">
            we specialize in crafting custom, high-performance websites that
            deliver results. Whether you need an e-commerce platform or a
            dynamic web app, we’re here to help your business thrive in the
            digital world.
          </p>

          <div className="hero-btns">
            <Link to={`${config.appUrl}contact`} className="btn-primary">
              Start Your Project →
            </Link>
            <Link to={`${config.appUrl}services`} className="btn-outline">
              Explore Services
            </Link>
          </div>
        </div>

        {/* <div className="hero-scroll">
          <span>Scroll</span>
          <div className="scroll-line"/>
        </div> */}
      </section>

      {/* ── MARQUEE ── */}
      <div className="marquee-section">
        <div className="marquee-track">
          {marqueeItems.map((item, i) => (
            <div className="marquee-item" key={i}>
              <span className="dot" />
              {item}
            </div>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="stats-band">
        {STATS.map((s) => (
          <div className="stat-item" key={s.label}>
            <div className="stat-num">
              <CountUp
                end={s.val}
                suffix={s.suffix}
                duration={2.5}
                enableScrollSpy
                scrollSpyOnce
              />
            </div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── SERVICES ── */}
      <section className="services-sect">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">What We Do</span>
            <h2 className="s-title">
              Services Built for <span className="g">Modern Brands</span>
            </h2>
            <p className="s-body">
              Three core pillars of digital excellence - each executed with
              precision, passion, and purpose.
            </p>
          </div>

          <div className="svc-grid">
            {SERVICES.map((s, i) => (
              <Link to={`${config.appUrl}${s.path}`} key={s.title}>
                <div className={`svc-card reveal d${i + 1}`} key={s.title}>
                  <div className="svc-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  {/* <Link to={`${config.appUrl}${s.path}`} className="svc-arrow">
                  Learn More → */}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="process-sect">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">How We Work</span>
            <h2 className="s-title">
              Our Proven <span className="g">Process</span>
            </h2>
          </div>
          <div className="process-grid">
            {PROCESS.map((p, i) => (
              <div
                className={`proc-card reveal d${i + 1}`}
                data-n={p.n}
                key={p.n}
              >
                <div className="proc-num">{p.n}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testi-sect">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Client Stories</span>
            <h2 className="s-title">
              Trusted by <span className="g">Industry Leaders</span>
            </h2>
          </div>
          <div className="testi-grid">
            {TESTIMONIALS.map((t, i) => (
              <div className={`testi-card reveal d${i + 1}`} key={t.name}>
                <div className="stars">
                  {"★★★★★".split("").map((s, j) => (
                    <span key={j}>{s}</span>
                  ))}
                </div>
                <p>{t.text}</p>
                <div className="reviewer">
                  <div className="rev-avatar">{t.init}</div>
                  <div>
                    <div className="rev-name">{t.name}</div>
                    <div className="rev-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="cta-sect">
        <div className="cta-glow" />
        <div
          className="container"
          style={{ position: "relative", textAlign: "center" }}
        >
          <div className="reveal">
            <span
              className="s-eye"
              style={{ justifyContent: "center", display: "block" }}
            >
              Ready to Start?
            </span>
            <h2 className="cta-title">
              Let's Build Something <span className="g">Extraordinary</span>
            </h2>
            <p className="cta-body">
              Join 150+ brands who have trusted CubeX Technology to bring their
              digital vision to life.
            </p>
            <div
              style={{
                display: "flex",
                gap: "1rem",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Link to={`${config.appUrl}contact`} className="btn-primary">
                Start Your Project →
              </Link>
              <Link
                to={`${config.appUrl}web-development-package`}
                className="btn-outline"
              >
                View Packages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
